import type React from 'react';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  deferTracking,
  sendGAEvent,
  sendGALinkClickEvent,
} from '@noths/polaris-client-google-analytics';

import {
  selectAreRequiredOptionsIncomplete,
  selectHasOptions,
  selectIsInEditMode,
  selectIsSubmitOptionsFormPending,
} from 'src/redux/basket/selectors';
import { submitOptionsForm } from 'src/redux/basket/thunks';
import { selectIsPersonalisable, selectProduct } from 'src/redux/product/selectors';
import type { AppThunkDispatch } from 'src/redux/store';
import type { ReduxApplicationState } from 'src/redux/types';
import type { BasketCTAButtonDispatchProps, BasketCTAButtonStateProps } from './BasketCTAButton';
import { MAIN_PAGE_CTA_ID } from './elementIDs';

type UseBasketCTAButton = BasketCTAButtonDispatchProps & BasketCTAButtonStateProps;

export const useBasketCTAButton = (): UseBasketCTAButton => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const editMode = useSelector(selectIsInEditMode);
  const hasOptions = useSelector(selectHasOptions);
  const loading = useSelector(selectIsSubmitOptionsFormPending);
  const personalisable = useSelector(selectIsPersonalisable);
  const purchasable = useSelector(
    (state: ReduxApplicationState) => selectProduct(state).purchasable,
  );
  const requiredFieldsIncomplete = useSelector(selectAreRequiredOptionsIncomplete);

  const onAddEditClick = useCallback(() => dispatch(submitOptionsForm()), [dispatch]);

  const onBuyGiftCard = useCallback((ev: React.MouseEvent) => {
    sendGALinkClickEvent(ev, {
      event: 'custom_event',
      event_action: 'Buy giftcard',
      event_category: 'Product page',
      event_label: 'not set',
    });
  }, []);

  const onVisible = useCallback(() => {
    const ctaButton: HTMLElement | null = document.getElementById(MAIN_PAGE_CTA_ID);

    deferTracking(() =>
      sendGAEvent({
        event: 'custom_event',
        event_category: 'Product page CTA',
        event_action: 'Impression',
        event_label: ctaButton?.textContent ?? '',
      }),
    );
  }, []);

  return {
    editMode,
    hasOptions,
    loading,
    onAddEditClick,
    onBuyGiftCard,
    onVisible,
    personalisable,
    purchasable,
    requiredFieldsIncomplete,
  };
};
